import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isEqualFormValues } from 'dpl/shared/form/utils';
import SmartImage from 'dpl/components/SmartImage';
import FullPageQuizQuestion from 'dpl/components/FullPageQuiz/FullPageQuizQuestion';

import puppyNoPreferencePng from 'dpl/assets/puppy_no_preference.png';

const DOG_INPUT_NAME = 'dog_id';
const MAX_PUPPIES_PER_ROW = 5;

export function QuizPuppyQuestionOption({
  imageSrc,
  isSelected,
  onChange,
  name,
  caption,
  litterName,
  statusText,
  title,
  value
}) {
  const htmlId = `puppy_preference-${value}`;

  return (
    <label
      className={classnames(
        'AdoptionApplicationQuestionOption__label transition db br1 pointer',
        {
          'AdoptionApplicationQuestionOption__label--selected': isSelected
        }
      )}
      htmlFor={htmlId}
    >
      <input
        checked={isSelected}
        className="AdoptionApplicationQuestionOption__input dn outline-0"
        id={htmlId}
        name={name}
        onClick={onChange}
        onChange={() => {}}
        type="radio"
        value={value}
      />
      <div className="mb2 aspect-ratio aspect-ratio--4x3 relative">
        <div className="aspect-ratio--object ">
          <SmartImage
            Tag="div"
            className="w-100 h-100 br1 br--top cover bg-center"
            src={imageSrc}
          />
          {caption && (
            <div className="absolute top-0 pv1 ph1 right-0 mw-100 w-auto">
              <p className="AdoptionApplicationQuestionOption__caption pv1 ph2 br2 bg-black-60 f1 fw-medium white ttc">
                {caption}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="ph4 pb4">
        <p className="fw-medium truncate">{title}</p>
        {litterName && <p className="mt2 db f2 mid-gray">{litterName}</p>}
        {statusText && (
          <p className="mt2 db f2 mid-gray truncate">{statusText}</p>
        )}
      </div>
    </label>
  );
}

QuizPuppyQuestionOption.propTypes = {
  imageSrc: PropTypes.string,
  isSelected: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  litterName: PropTypes.string,
  statusText: PropTypes.string,
  title: PropTypes.string,
  caption: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

QuizPuppyQuestionOption.defaultProps = {
  imageSrc: null,
  isSelected: false,
  name: null,
  litterName: null,
  statusText: null,
  title: null,
  caption: null,
  value: null
};

export default function QuizPuppyQuestion({
  availablePuppies,
  formState,
  handleNextClick,
  onChange,
  isValid,
  programName
}) {
  const selectedPuppy = formState[DOG_INPUT_NAME] || '';
  const [puppyDisplayCount, updatePuppyDisplayCount] = useState(
    availablePuppies.length > 9 ? 9 : availablePuppies.length
  );
  const hasOneRowOnDesktop = availablePuppies.length < MAX_PUPPIES_PER_ROW - 1;

  return (
    <div className="AdoptionApplicationQuizPuppyQuestion">
      <FullPageQuizQuestion
        className="ph24"
        handleNextClick={handleNextClick}
        isValid={isValid}
        variant="wide"
        title="Choose your puppy preference"
        subtitle={`Let ${programName} know which puppy you’re interested in. They’ll consider your puppy preference as you work together to find the perfect match. If you don’t have a preference yet, that’s OK!`}
        subtitleClassName="mw-50-md center"
      >
        <ul
          className={classnames('row', {
            'justify-center-l': hasOneRowOnDesktop
          })}
        >
          {availablePuppies.map((puppy, idx) => {
            const leftPadding =
              !hasOneRowOnDesktop && idx % MAX_PUPPIES_PER_ROW === 0;
            return (
              <li
                className={classnames('mb6 col-xl-2 col-lg-3 col-md-4 col-6 ', {
                  'offset-xl-1': leftPadding,
                  dn: idx >= puppyDisplayCount
                })}
                key={idx}
              >
                <QuizPuppyQuestionOption
                  isSelected={isEqualFormValues(selectedPuppy, puppy.id)}
                  name={puppy.name}
                  onChange={() => {
                    onChange(puppy);
                  }}
                  title={puppy.name}
                  litterName={puppy.litter_name}
                  statusText={puppy.status_for_buyer_application}
                  value={puppy.id}
                  imageSrc={
                    puppy.profile_photo_url_4x3 || puppy.placeholder_photo_url
                  }
                  caption={puppy.gender}
                />
              </li>
            );
          })}
          <li
            className={classnames('mb6 col-xl-2 col-lg-3 col-md-4 col-6 ', {
              'offset-xl-1': puppyDisplayCount % 5 === 0
            })}
          >
            <QuizPuppyQuestionOption
              isSelected={isEqualFormValues(selectedPuppy, '')}
              name="No preference"
              onChange={() => {
                onChange(null);
              }}
              title="No preference"
              litterName="Work with your breeder to get matched"
              value=""
              imageSrc={puppyNoPreferencePng}
            />
          </li>
        </ul>
        <div>
          {puppyDisplayCount < availablePuppies.length && (
            <button
              type="button"
              className="purple"
              onClick={() => updatePuppyDisplayCount(availablePuppies.length)}
            >
              Show More
            </button>
          )}
        </div>
      </FullPageQuizQuestion>
    </div>
  );
}

QuizPuppyQuestion.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  availablePuppies: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  formState: PropTypes.object.isRequired,
  handleNextClick: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
  programName: PropTypes.string.isRequired
};
